import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Logo from '@/components/feature/Logo';
import { useAudioPlayer } from '@/context/AudioContext';

const navLinks = [
  { label: 'Inicio', href: '#inicio' },
  { label: 'Explorar Radios', href: '/explorar', isRoute: true },
  { label: 'Rankings', href: '/rankings', isRoute: true },
  { label: 'Clubes', href: '/clubes', isRoute: true },
  { label: 'Competencias', href: '/competencias', isRoute: true },
  { label: 'Radios Destacadas', href: '#destacadas' },
  { label: 'Planes', href: '/pricing', isRoute: true },
];

const mobileExtraLinks = [
  { label: 'Eventos', href: '/eventos', isRoute: true, icon: 'ri-calendar-line' },
  { label: 'Mapa Mundial', href: '/mapa-mundial', isRoute: true, icon: 'ri-global-line' },
  { label: 'Centro Social', href: '/centro-social', isRoute: true, icon: 'ri-group-line' },
  { label: 'Gamificación', href: '/gamificacion', isRoute: true, icon: 'ri-trophy-line' },
  { label: 'Club VIP', href: '/club-vip', isRoute: true, icon: 'ri-vip-crown-line' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [drawerAnimating, setDrawerAnimating] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { currentStation, isPlaying, toggle } = useAudioPlayer();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const openDrawer = () => {
    setMobileMenuOpen(true);
    setDrawerAnimating(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDrawerAnimating(false);
      });
    });
  };

  const closeDrawer = () => {
    setDrawerAnimating(true);
    setTimeout(() => {
      setMobileMenuOpen(false);
    }, 250);
  };

  const handleNavClick = (href: string, isRoute?: boolean) => {
    closeDrawer();
    setTimeout(() => {
      if (isRoute) {
        navigate(href);
        return;
      }
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 280);
  };

  const handlePlayerTap = () => {
    if (currentStation) {
      toggle();
    } else {
      navigate('/app');
    }
  };

  // ────────────────────────────────────────────────────────────
  //  DESKTOP HEADER  —  sin cambios
  // ────────────────────────────────────────────────────────────
  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-xl shadow-sm'
            : 'bg-transparent'
        }`}
      >
        {/* ============ DESKTOP ============ */}
        <div className="hidden md:block w-full px-4 md:px-6 lg:px-8 xl:px-5">
          <div className="flex items-center justify-between h-16 md:h-18">
            <a
              href="#inicio"
              onClick={(e) => { e.preventDefault(); handleNavClick('#inicio'); }}
              className="flex items-center cursor-pointer shrink-0"
            >
              <Logo size="md" className={scrolled ? '' : 'brightness-125'} />
            </a>

            <nav className="hidden lg:flex items-center gap-1 lg:overflow-x-auto xl:gap-0 xl:overflow-visible flex-1 min-w-0 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.isRoute ? link.href : link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.href, link.isRoute); }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer xl:px-1 xl:py-1.5 xl:text-xs xl:tracking-tight ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  }`}
                >
                  {link.label}
                </a>
              ))}
              <Link to="/eventos" className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer xl:px-1 xl:py-1.5 xl:text-xs xl:tracking-tight ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  }`}>
                <i className="ri-calendar-line mr-1 xl:mr-0.5"></i>Eventos
              </Link>
              <Link to="/mapa-mundial" className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer xl:px-1 xl:py-1.5 xl:text-xs xl:tracking-tight ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  }`}>
                <i className="ri-global-line mr-1 xl:mr-0.5"></i>Mapa
              </Link>
              <Link to="/centro-social" className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer xl:px-1 xl:py-1.5 xl:text-xs xl:tracking-tight ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  }`}>
                <i className="ri-group-line mr-1 xl:mr-0.5"></i>Centro Social
              </Link>
              <Link to="/gamificacion" className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer xl:px-1 xl:py-1.5 xl:text-xs xl:tracking-tight ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  }`}>
                <i className="ri-trophy-line mr-1 xl:mr-0.5"></i>Gamificación
              </Link>
              <Link to="/club-vip" className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer xl:px-1 xl:py-1.5 xl:text-xs xl:tracking-tight ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  }`}>
                <i className="ri-vip-crown-line mr-1 xl:mr-0.5"></i>Club VIP
              </Link>
            </nav>

            <div className="hidden lg:flex items-center gap-2 ml-3 shrink-0 xl:gap-1 xl:ml-2">
              <button
                onClick={() => navigate('/app')}
                className="hidden xl:flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer bg-background-50/15 hover:bg-background-50/25 text-background-50 border border-background-50/20 xl:px-1.5 xl:py-1.5 xl:text-xs xl:gap-1"
              >
                <i className="ri-smartphone-line"></i>
                Abrir App
              </button>
              <button
                onClick={() => navigate('/listener')}
                className={`hidden xl:flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer xl:px-1.5 xl:py-1.5 xl:text-xs xl:gap-1 ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                <i className="ri-user-3-line"></i>
                Centro de Oyentes
              </button>
              <button
                onClick={() => {
                  Swal.fire({
                    title: 'Iniciar Sesión',
                    text: 'El inicio de sesión estará disponible próximamente con Supabase Auth.',
                    icon: 'info',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#f59e0b',
                    customClass: {
                      popup: 'rounded-2xl',
                      title: 'font-bold text-sm',
                      htmlContainer: 'text-xs',
                      confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                    },
                    buttonsStyling: false,
                  });
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer xl:px-1.5 xl:py-1.5 xl:text-xs ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                Iniciar Sesión
              </button>
              <button
                onClick={() => navigate('/register-radio')}
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20 xl:px-2.5 xl:py-1.5 xl:text-xs"
              >
                Registrar Mi Radio
              </button>
            </div>
          </div>
        </div>

        {/* ============ MOBILE NATIVO ============ */}
        <div className="md:hidden w-full px-3">
          <div className="flex items-center justify-between h-14">
            {/* ── Hamburguesa ── */}
            <button
              onClick={openDrawer}
              className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors cursor-pointer shrink-0 ${
                scrolled
                  ? 'text-foreground-800 hover:bg-background-100'
                  : 'text-background-50 hover:bg-background-50/10'
              }`}
              aria-label="Abrir menú"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>

            {/* ── Logo ── */}
            <a
              href="#inicio"
              onClick={(e) => { e.preventDefault(); handleNavClick('#inicio'); }}
              className="flex items-center cursor-pointer shrink-0 mx-2"
            >
              <Logo size="sm" className={scrolled ? '' : 'brightness-110'} />
            </a>

            {/* ── Acciones rápidas ── */}
            <div className="flex items-center gap-1">
              {/* Buscar */}
              <button
                onClick={() => navigate('/buscar')}
                className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
                aria-label="Buscar"
              >
                <i className="ri-search-line text-lg"></i>
              </button>

              {/* Notificaciones */}
              <button
                onClick={() => navigate('/centro-notificaciones')}
                className={`relative w-10 h-10 flex items-center justify-center rounded-xl transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
                aria-label="Notificaciones"
              >
                <i className="ri-notification-3-line text-lg"></i>
                <span className="absolute top-2 right-2 w-2 h-2 bg-accent-500 rounded-full ring-2 ring-background-50"></span>
              </button>

              {/* Player quick access */}
              <button
                onClick={handlePlayerTap}
                className={`relative flex items-center gap-1.5 h-10 px-2 rounded-xl transition-all duration-200 cursor-pointer ${
                  currentStation
                    ? isPlaying
                      ? 'bg-accent-500/10 text-accent-600 border border-accent-200/60'
                      : 'bg-background-100 text-foreground-700 border border-background-200'
                    : scrolled
                      ? 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
                } ${currentStation ? 'min-w-[60px]' : 'w-10 justify-center'}`}
                aria-label={currentStation ? (isPlaying ? 'Pausar' : 'Reproducir') : 'Abrir Player'}
              >
                {currentStation ? (
                  <>
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isPlaying ? 'bg-accent-500 animate-pulse' : 'bg-foreground-400'}`}></span>
                    <span className="text-[11px] font-semibold truncate max-w-[56px] leading-tight">
                      {currentStation.frequency || currentStation.name}
                    </span>
                    <i className={`text-sm shrink-0 ${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
                  </>
                ) : (
                  <i className="ri-music-line text-lg"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ============ MOBILE DRAWER (slide-in desde izquierda) ============ */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-foreground-950/60 transition-opacity duration-250 ${drawerAnimating ? 'opacity-0' : 'opacity-100'}`}
            onClick={closeDrawer}
          ></div>

          {/* Drawer panel */}
          <div
            ref={drawerRef}
            className={`absolute top-0 left-0 bottom-0 w-[85%] max-w-[340px] bg-background-50 shadow-2xl flex flex-col transition-transform duration-250 ease-out ${drawerAnimating ? '-translate-x-full' : 'translate-x-0'}`}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 h-14 border-b border-background-200 shrink-0">
              <Logo size="sm" />
              <button
                onClick={closeDrawer}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Cerrar menú"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Drawer body — scrollable nav */}
            <div className="flex-1 overflow-y-auto overscroll-contain py-3 px-3">
              {/* ── Navegación principal ── */}
              <div className="mb-3">
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-foreground-400 mb-1">
                  Navegación
                </p>
                {navLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => handleNavClick(link.href, link.isRoute)}
                    className="w-full flex items-center gap-3 px-3 py-3 text-left text-foreground-800 hover:bg-background-100 rounded-xl text-[15px] font-medium transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 shrink-0">
                      <i className={`text-base ${
                        link.label === 'Inicio' ? 'ri-home-4-line' :
                        link.label.includes('Explorar') ? 'ri-compass-3-line' :
                        link.label === 'Rankings' ? 'ri-bar-chart-2-line' :
                        link.label === 'Clubes' ? 'ri-shield-star-line' :
                        link.label === 'Competencias' ? 'ri-sword-line' :
                        link.label.includes('Destacadas') ? 'ri-star-line' :
                        link.label === 'Planes' ? 'ri-price-tag-3-line' :
                        'ri-arrow-right-line'
                      }`}></i>
                    </span>
                    {link.label}
                  </button>
                ))}
              </div>

              <div className="border-t border-background-200/70 my-1"></div>

              {/* ── Más secciones ── */}
              <div className="mt-3 mb-3">
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-foreground-400 mb-1">
                  Más
                </p>
                {mobileExtraLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => handleNavClick(link.href, link.isRoute)}
                    className="w-full flex items-center gap-3 px-3 py-3 text-left text-foreground-700 hover:bg-background-100 rounded-xl text-[15px] font-medium transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-400 shrink-0">
                      <i className={`${link.icon} text-base`}></i>
                    </span>
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Drawer footer — acciones */}
            <div className="border-t border-background-200 px-3 py-3 space-y-2 shrink-0">
              <button
                onClick={() => { closeDrawer(); setTimeout(() => navigate('/app'), 280); }}
                className="w-full flex items-center gap-3 px-3 py-3 text-left bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-xl text-[15px] font-semibold transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 shrink-0">
                  <i className="ri-smartphone-line text-base"></i>
                </span>
                Abrir App RFW
              </button>

              <button
                onClick={() => { closeDrawer(); setTimeout(() => navigate('/listener'), 280); }}
                className="w-full flex items-center gap-3 px-3 py-3 text-left text-foreground-700 hover:bg-background-100 rounded-xl text-[15px] font-medium transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-400 shrink-0">
                  <i className="ri-user-3-line text-base"></i>
                </span>
                Centro de Oyentes
              </button>

              <button
                onClick={() => {
                  closeDrawer();
                  setTimeout(() => {
                    Swal.fire({
                      title: 'Iniciar Sesión',
                      text: 'El inicio de sesión estará disponible próximamente con Supabase Auth.',
                      icon: 'info',
                      confirmButtonText: 'Entendido',
                      confirmButtonColor: '#f59e0b',
                      customClass: {
                        popup: 'rounded-2xl',
                        title: 'font-bold text-sm',
                        htmlContainer: 'text-xs',
                        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                      },
                      buttonsStyling: false,
                    });
                  }, 280);
                }}
                className="w-full flex items-center gap-3 px-3 py-3 text-left text-foreground-700 hover:bg-background-100 rounded-xl text-[15px] font-medium transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-400 shrink-0">
                  <i className="ri-login-box-line text-base"></i>
                </span>
                Iniciar Sesión
              </button>

              <button
                onClick={() => { closeDrawer(); setTimeout(() => navigate('/register-radio'), 280); }}
                className="w-full flex items-center justify-center gap-2 px-3 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-[15px] font-semibold transition-colors cursor-pointer"
              >
                <i className="ri-radio-line text-lg"></i>
                Registrar Mi Radio
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}